import type { Camera } from '../services/camerasApi';
import type { Line } from '../services/linesApi';
import type { Policy } from '../services/policiesApi';
import type { Zone } from '../services/zonesApi';
import {
  getPolicyDeploymentMetadata,
  getPolicyTroubleshootingMetadata,
  type PolicyDeploymentState,
  type PolicyTroubleshootingState,
  type StatusMetadata,
} from './statusMetadata';

export type PolicyBucket = 'attention' | 'deploying' | 'pending_deploy' | 'healthy' | 'draft';

/**
 * Map a backend deploy `reason_code` to a friendly, user-actionable string.
 *
 * Returns `null` when the code is unknown so the caller can fall back to the
 * raw API message. Used by the deploy snackbar to give operators a head-start
 * on diagnosis instead of leaking opaque enum values.
 */
export function formatDeployReasonCode(code: unknown): string | null {
  const c = String(code ?? '').trim();
  if (!c) return null;
  switch (c) {
    case 'zone_plane_not_converged':
      return 'Zone bundle on the edge does not match the cloud yet. Cloud will retry; if it persists, check edge runtime status.';
    case 'zone_reference_missing':
      return 'Policy references a zone that no longer exists for the targeted camera.';
    case 'policy_geometry_lint_failed':
      return 'Policy geometry has lint errors. Open the lint dialog for details.';
    case 'BUNDLE_VERSION_REGRESSION':
      return 'Edge has a newer bundle than cloud expected. Cloud is recovering; retry in a few seconds.';
    case 'EDGE_RUNTIME_HASH_MISMATCH':
      return 'Edge runtime is reporting a different bundle hash than the cloud desired state. Inspect the edge /runtime/status response.';
    case 'EDGE_RUNTIME_VERSION_MISMATCH':
      return 'Edge runtime reports an older bundle_version than the cloud desired state.';
    case 'EDGE_RUNTIME_ZONE_MISSING':
      return 'Edge runtime has no entry for this camera in config_versions.zones; the bundle never landed.';
    case 'EDGE_EFFECTIVE_ZONES_MISSING':
      return 'Edge confirmed the bundle metadata but is missing the effective_zones artifact for this camera.';
    default:
      return null;
  }
}

export interface PolicyTroubleshootingView {
  policy: Policy;
  bucket: PolicyBucket;
  state: PolicyTroubleshootingState;
  statusMeta: StatusMetadata;
  deploymentMeta: StatusMetadata;
  deploymentState: PolicyDeploymentState;
  summary: string;
  detail: string;
  targetLabel: string;
  deploymentLabel: string;
  zoneSummary: string;
  lineSummary: string;
  issueList: string[];
  issueCode: string;
  targetCameraId: string;
  targetDeviceId: string;
  recommendedAction: 'fix' | 'deploy' | 'activate' | 'details' | 'none';
}

function _clean(v: unknown): string {
  return String(v ?? '').trim();
}

function _deploymentStateForPolicy(policy: Policy): PolicyDeploymentState {
  if (policy.deployment_state) return policy.deployment_state as PolicyDeploymentState;
  if (policy.status === 'active') {
    // Fallback when older API responses omit deployment_state (should be rare).
    if (policy.last_ack_at) return 'DEPLOYED';
    const ackedAt = (policy.deployment as { acked_at?: string } | undefined)?.acked_at;
    if (ackedAt) return 'DEPLOYED';
    const hasDeployArtifact = Boolean(
      policy.deployment?.deployment_id || policy.last_deployed_at || policy.last_ack_at,
    );
    return hasDeployArtifact ? 'DEPLOYING' : 'DRAFT';
  }
  return 'DRAFT';
}

function _detectState(args: {
  policy: Policy;
  targetCameraId: string;
  targetDeviceId: string;
  hasCameraBinding: boolean;
  matchingZones: Zone[];
  scopeZoneIds: string[];
  targetLine: Line | null;
}): PolicyTroubleshootingState {
  const { policy, targetCameraId, targetDeviceId, hasCameraBinding, matchingZones, scopeZoneIds, targetLine } = args;
  const deploymentState = _deploymentStateForPolicy(policy);
  const reasonCode = _clean(policy.deployment_reason_code).toLowerCase();
  const reason = _clean(policy.deployment_reason).toLowerCase();
  const migrationCode = _clean(policy.migration_error_code).toLowerCase();

  if ((policy.status === 'active' || deploymentState === 'DEPLOYING') && targetCameraId && !hasCameraBinding) {
    return 'orphaned_camera';
  }
  if ((policy.status === 'active' || deploymentState === 'DEPLOYING') && (!targetCameraId || !targetDeviceId)) {
    return 'missing_target';
  }
  if (deploymentState === 'FAILED_TIMEOUT') {
    return 'deployment_timeout';
  }
  if (
    deploymentState === 'FAILED' ||
    reasonCode.includes('failed') ||
    reasonCode.includes('nack') ||
    reason.includes('failed') ||
    reason.includes('rejected')
  ) {
    return 'deployment_failed';
  }
  if (
    migrationCode.includes('lint') ||
    reasonCode.includes('lint') ||
    reason.includes('lint') ||
    reason.includes('validation')
  ) {
    return 'lint_blocked';
  }
  if (
    reasonCode.includes('zone') ||
    migrationCode.includes('zone') ||
    reason.includes('zone') ||
    (scopeZoneIds.length > 0 && matchingZones.length < scopeZoneIds.length) ||
    (policy.policy_type === 'line_crossing' && !targetLine)
  ) {
    return 'zone_issue';
  }
  if (deploymentState === 'DEPLOYING') {
    return 'deploying';
  }
  if (policy.status !== 'active' || policy.enabled === false) {
    return 'draft';
  }
  if (deploymentState === 'DEPLOYED') {
    return 'healthy';
  }
  if (deploymentState === 'DRAFT') {
    return 'pending_deploy';
  }
  return 'attention';
}

export function buildPolicyTroubleshootingView(args: {
  policy: Policy;
  cameras: Camera[];
  zones: Zone[];
  lines: Line[];
}): PolicyTroubleshootingView {
  const { policy, cameras, zones, lines } = args;
  const scopeCameraIds = Array.isArray(policy.scope?.camera_ids) ? policy.scope.camera_ids : [];
  const scopeZoneIds = Array.isArray(policy.scope?.zone_ids) ? policy.scope.zone_ids : [];
  const targetDeviceId = _clean(policy.target?.edge_device_id || policy.edge_device_id || policy.scope?.edge_device_id);
  const targetCameraId = _clean(policy.target?.camera_id || policy.camera_id || scopeCameraIds[0]);
  const targetCamera = cameras.find(
    (camera) => _clean(camera.camera_id) === targetCameraId && (!targetDeviceId || _clean(camera.edge_device_id) === targetDeviceId),
  ) || null;
  const matchingZones = zones.filter((zone) => {
    if (!scopeZoneIds.includes(zone.zone_id)) return false;
    if (targetCameraId && _clean(zone.camera_id) !== targetCameraId) return false;
    if (targetDeviceId && _clean((zone as any).device_id) !== targetDeviceId) return false;
    return true;
  });
  const targetLineId = _clean((policy.config as any)?.line_id || (policy.config as any)?.line?.line_id);
  const targetLine = targetLineId
    ? lines.find((line) => _clean(line.line_id || (line as any).id) === targetLineId) || null
    : null;
  const deploymentState = _deploymentStateForPolicy(policy);
  const state = _detectState({
    policy,
    targetCameraId,
    targetDeviceId,
    hasCameraBinding: Boolean(targetCamera),
    matchingZones,
    scopeZoneIds,
    targetLine,
  });
  const deploymentMeta = getPolicyDeploymentMetadata(deploymentState);
  const statusMeta = getPolicyTroubleshootingMetadata(state);

  const issueList: string[] = [];
  if (state === 'orphaned_camera') {
    issueList.push(targetCameraId ? `Camera "${targetCameraId}" is no longer registered.` : 'Camera binding is missing.');
  }
  if (state === 'missing_target') {
    issueList.push('This policy does not have a complete edge device and camera target.');
  }
  if (scopeZoneIds.length > 0 && matchingZones.length < scopeZoneIds.length) {
    const missingCount = scopeZoneIds.length - matchingZones.length;
    issueList.push(`${missingCount} referenced zone${missingCount === 1 ? '' : 's'} could not be matched to the current target.`);
  }
  if (policy.policy_type === 'line_crossing' && !targetLine) {
    issueList.push('The configured line could not be found for this policy.');
  }
  if (_clean(policy.deployment_reason)) {
    issueList.push(_clean(policy.deployment_reason));
  }
  if (_clean(policy.migration_error_code)) {
    issueList.push(`Migration issue: ${_clean(policy.migration_error_code)}`);
  }

  const targetParts = [targetDeviceId, targetCameraId].filter(Boolean);
  const targetLabel = targetParts.length > 0
    ? targetParts.join(' / ')
    : 'No target camera selected';

  const zoneSummary = policy.policy_type === 'line_crossing'
    ? (targetLine ? `Line ${targetLine.name || targetLine.line_id}` : 'Line target missing')
    : scopeZoneIds.length > 0
      ? `${matchingZones.length}/${scopeZoneIds.length} zones resolved`
      : (policy.scope?.geometry_mode === 'whole_view' ? 'Whole camera view' : 'No zones');

  const lineSummary = targetLine
    ? `${targetLine.name || targetLine.line_id}`
    : (targetLineId ? 'Missing line' : '');

  const summary = statusMeta.label;
  // Prefer server-provided deployment_reason (e.g. "Policy changed since last deployment")
  // over the generic pending_deploy blurb so operators are not misled after a successful deploy + edit.
  const serverDeployReason = _clean(policy.deployment_reason);
  let detail = issueList[0] || statusMeta.reason;
  if (state === 'pending_deploy' && serverDeployReason) {
    detail = serverDeployReason;
  }

  let bucket: PolicyBucket = 'draft';
  if (state === 'healthy') bucket = 'healthy';
  else if (state === 'deploying') bucket = 'deploying';
  else if (state === 'pending_deploy') bucket = 'pending_deploy';
  else if (state !== 'draft') bucket = 'attention';

  let recommendedAction: PolicyTroubleshootingView['recommendedAction'] = 'details';
  if (state === 'draft') {
    recommendedAction = policy.status === 'active' ? 'deploy' : 'activate';
  } else if (state === 'pending_deploy') {
    recommendedAction = 'deploy';
  } else if (state === 'healthy') {
    recommendedAction = 'deploy';
  } else if (state === 'deploying') {
    recommendedAction = 'none';
  } else if (state === 'deployment_failed' || state === 'deployment_timeout') {
    recommendedAction = 'deploy';
  } else if (state === 'missing_target' || state === 'orphaned_camera' || state === 'lint_blocked' || state === 'zone_issue') {
    recommendedAction = 'fix';
  }

  return {
    policy,
    bucket,
    state,
    statusMeta,
    deploymentMeta,
    deploymentState,
    summary,
    detail,
    targetLabel,
    deploymentLabel: _clean(policy.deployment_reason) || deploymentMeta.reason,
    zoneSummary,
    lineSummary,
    issueList,
    issueCode: _clean(policy.deployment_reason_code || policy.migration_error_code || state),
    targetCameraId,
    targetDeviceId,
    recommendedAction,
  };
}
